import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

function cancelScheduledIdleCallback(callbackId) {
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(callbackId);
  } else {
    window.clearTimeout(callbackId);
  }
}

export class AnchoredPopoverComponent extends Component {
  requiredRefs = ['popover', 'trigger'];
  interaction_delay = 200;
  #popoverTrigger =   (null);
  #connectionController =   (null);
  #idleCallbackId =   (null);
  #connectionEpoch = 0;

  #onTriggerEnter = () => {
    const { trigger, popover } = this.refs;
    this.#clearPopoverTrigger();
    trigger.dataset.hoverActive = 'true';
    if (!popover.matches(':popover-open')) {
      this.#popoverTrigger = setTimeout(() => {
        this.#popoverTrigger = null;
        if (!this.isConnected) return;
        if (trigger.matches('[data-hover-active]')) popover.showPopover();
      }, this.interaction_delay);
    }
  };

  #onTriggerLeave = () => {
    const { trigger, popover } = this.refs;
    delete trigger.dataset.hoverActive;
    this.#clearPopoverTrigger();
    if (popover.matches(':popover-open')) {
      this.#popoverTrigger = setTimeout(() => {
        this.#popoverTrigger = null;
        if (!this.isConnected) return;
        popover.hidePopover();
      }, this.interaction_delay);
    }
  };

  #onPopoverEnter = () => {
    this.#clearPopoverTrigger();
  };

  #onPopoverLeave = () => {
    const { popover } = this.refs;
    this.#clearPopoverTrigger();
    this.#popoverTrigger = setTimeout(() => {
      this.#popoverTrigger = null;
      if (!this.isConnected) return;
      popover.hidePopover();
    }, this.interaction_delay);
  };

  #onPopoverToggle = (event) => {
    const evt =   (event);
    window[evt.newState === 'open' ? 'addEventListener' : 'removeEventListener']('resize', this.#resizeListener);
  };

  #onFallbackToggle = () => {
    this.#updatePosition();
  };

  #updatePosition = async () => {
    if (!this.isConnected) return;

    const { popover, trigger } = this.refs;
    if (!popover || !trigger) return;

    const triggerPositions = trigger.getBoundingClientRect();
    const verticalPositionAnchor = this.#getVerticalPositionAnchor();
    const verticalPositions = (verticalPositionAnchor ?? trigger).getBoundingClientRect();

    const fromLeft = triggerPositions.left;
    const fromRight = window.innerWidth - triggerPositions.right;
    const isRTL = getComputedStyle(popover).direction === 'rtl';

    popover.style.setProperty('--anchor-top', `${verticalPositions.top}`);
    popover.style.setProperty('--anchor-right', `${fromRight}`);
    popover.style.setProperty('--anchor-bottom', `${window.innerHeight - verticalPositions.bottom}`);
    popover.style.setProperty('--anchor-left', `${fromLeft}`);
    popover.style.setProperty('--anchor-inline-start', `${isRTL ? fromRight : fromLeft}`);
    popover.style.setProperty('--anchor-height', `${verticalPositions.height}`);
    popover.style.setProperty('--anchor-width', `${triggerPositions.width}`);
  };

  #getVerticalPositionAnchor() {
    const internalAnchor = this.refs.verticalPositionAnchor;
    if (internalAnchor instanceof HTMLElement) return internalAnchor;

    const anchorName = this.dataset.verticalPositionAnchor?.trim();
    if (!anchorName) return null;

    const selector = `[data-anchor-name="${CSS.escape(anchorName)}"]`;
    const scopedAnchor = this.#queryVerticalPositionAnchor(selector);
    if (this.#isUsableExternalVerticalPositionAnchor(scopedAnchor)) return scopedAnchor;

    const root = this.getRootNode();
    const anchor = root instanceof Document || root instanceof ShadowRoot ? root.querySelector(selector) : null;

    return this.#isUsableExternalVerticalPositionAnchor(anchor) ? anchor : null;
  }

  #queryVerticalPositionAnchor(selector) {
    const scope = this.closest('[data-anchor-scope], tr, li, section, article, form');
    if (!(scope instanceof HTMLElement)) return null;

    const anchor = scope.querySelector(selector);

    return anchor instanceof HTMLElement ? anchor : null;
  }

  #isUsableExternalVerticalPositionAnchor(anchor) {
    if (!(anchor instanceof HTMLElement)) return false;

    const rect = anchor.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  }

  #resizeListener = debounce(() => {
    if (!this.isConnected) return;

    const popover =   (this.refs.popover);
    if (popover && popover.matches(':popover-open')) {
      popover.hidePopover();
    }
  }, 100);

  openDialog(dialogId, event) {
    event.preventDefault();

    const { popover, trigger } = this.refs;
    const triggerAriaControls = trigger.getAttribute('aria-controls');

    this.#clearPopoverTrigger();

    delete trigger.dataset.hoverActive;

    if (popover.matches(':popover-open')) {
      popover.hidePopover();
    }

    const dialog =   (
      document.getElementById(dialogId)
    );

    if (typeof dialog?.showDialog !== 'function') return;

    dialog.addEventListener(
      'dialog:close',
      () => {
        const focusTarget = trigger.isConnected ? trigger : getTriggerByAriaControls(triggerAriaControls);
        focusTarget?.focus();
      },
      { once: true, signal: this.#connectionController?.signal }
    );

    dialog.showDialog();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#connectionController?.abort();
    const connectionController = new AbortController();
    this.#connectionController = connectionController;
    const connectionEpoch = ++this.#connectionEpoch;
    this.#cancelIdleCallback();

    const { popover, trigger } = this.refs;
    if (this.dataset.closeOnResize) {
      popover.addEventListener('beforetoggle', this.#onPopoverToggle, { signal: connectionController.signal });
    }
    if (this.dataset.hoverTriggered) {
      trigger.addEventListener('pointerenter', this.#onTriggerEnter, { signal: connectionController.signal });
      trigger.addEventListener('pointerleave', this.#onTriggerLeave, { signal: connectionController.signal });
      popover.addEventListener('pointerenter', this.#onPopoverEnter, { signal: connectionController.signal });
      popover.addEventListener('pointerleave', this.#onPopoverLeave, { signal: connectionController.signal });
    }
    if (!CSS.supports('position-anchor: --trigger')) {
      popover.addEventListener('beforetoggle', this.#onFallbackToggle, { signal: connectionController.signal });
      const idleCallbackId = requestIdleCallback(() => {
        if (this.#idleCallbackId === idleCallbackId) this.#idleCallbackId = null;
        if (!this.isConnected || this.#connectionEpoch !== connectionEpoch) return;

        this.#updatePosition();
      });
      this.#idleCallbackId = idleCallbackId;
    }
  }

  disconnectedCallback() {
    this.#connectionEpoch += 1;
    this.#connectionController?.abort();
    this.#connectionController = null;
    this.#cancelIdleCallback();
    this.#clearPopoverTrigger();
    this.#resizeListener.cancel();
    const trigger = this.refs.trigger;
    if (trigger instanceof HTMLElement) delete trigger.dataset.hoverActive;

    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
  }

  #cancelIdleCallback() {
    if (this.#idleCallbackId === null) return;

    cancelScheduledIdleCallback(this.#idleCallbackId);
    this.#idleCallbackId = null;
  }

  #clearPopoverTrigger() {
    if (this.#popoverTrigger === null) return;

    clearTimeout(this.#popoverTrigger);
    this.#popoverTrigger = null;
  }
}

async function registerAnchoredPopover() {
  await Theme.popoverPolyfillReady;

  if (!customElements.get('anchored-popover-component')) {
    customElements.define('anchored-popover-component', AnchoredPopoverComponent);
  }
}

registerAnchoredPopover();

function getTriggerByAriaControls(ariaControls) {
  if (!ariaControls) return null;

  const trigger = document.querySelector(`[aria-controls="${CSS.escape(ariaControls)}"]`);

  return trigger instanceof HTMLElement && trigger.isConnected ? trigger : null;
}
